import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Table, TableHead, TableRow, TableCell, TableBody, Box, Typography, useMediaQuery } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import { useTheme } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';

import { DialogUI, InputUI, ButtonUI, AutoCompleteSelectUI } from '../../Interface';
import ProductionBatchDialog from './ProductionBatchDialog';
import { DocumentType } from '../../../config/DocumentType';
import productionService from '../../../service/production.service';
import itemService from '../../../service/item.service';
import { cleanValues } from '../../../utils/helper';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import { InvManagementMethod } from '../../../config/itemGroup';
import { downloadFile, printFromResponse } from '../../../utils/download';
import { subtract } from 'mathjs';
import ProductionReceiptMobileCards from './ProductionReceiptMobileCards';

const ProductionReceiptDialog = ({
  open,
  onClose,
  autocompleteData,
  cardData,
  mode,
  handleInterruptOperation,
  handleFinishOperation,
  autoCompleteOptions,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const ui = theme.palette.ui;
  const labelPrintCellSx = { backgroundColor: '#f5f5f5' };
  const labelQuantityInputSx = {
    '& .MuiInputBase-root': {
      backgroundColor: '#ffffff',
    },
  };
  const [docLines, setDocLines] = useState([]);
  const [batchLine, setBatchLine] = useState(null);
  const [selectedValues, setSelectedValues] = useState({});
  const [printingLineId, setPrintingLineId] = useState(null);
  const [isPosting, setIsPosting] = useState(false);

  const defReportData = { scrapQuantity: '', scrapReasonId: null, description: '' };
  const [reportData, setReportData] = useState(defReportData);

  useEffect(() => {
    if (!open) {
      setReportData(defReportData);
      setIsPosting(false);
    }
  }, [open]);

  const defaultBatchLine = {
    id: null,
    batchSn: '',
    lot: '',
    supplierId: '',
    expDate: null,
    mnfDate: null,
    batchAttribute1: '',
    batchAttribute2: '',
    comment: '',
    price: '',
    other: '',
    quantity: '',
  };

  const defaultDocumentLine = {
    lineStatus: 'C',
    itemCode: null,
    itemName: null,
    itemCodeInBp: null,
    qty: null,
    cplQty: null,
    opnQty: null,
    quantityUnitId: null,
    quantityUnitCode: null,
    quantityUnitName: null,
    whsCode: null,
    toWhsCode: null,
    unitPrice: null,
    currencyCode: null,
    vatCode: null,
    vatPercent: null,
    vatValue: null,
    netPrice: null,
    grossPrice: null,
    dueDate: dayjs(),
    vatValueInOwnCur: null,
    netPriceInOwnCur: null,
    grossPriceInOwnCur: null,
    weightChangeNumber: null,
    weight: null,
    projectId: null,
    costCenterCode: null,
    itemReference: null,
    internalNote: '',
    bpNote: '',
    baseId: null,
    baseLine: null,
    baseDocnum: null,
    baseDocType: null,
    docLineBatchSn: [],
    docLineBin: [],
    labelQuantity: 1,
  };

  const defaultValues = {
    docTypeId: DocumentType.ReceiptFromProduction,
    docStatus: 'C',
    stornoId: null,
    docDate: dayjs(),
    issueDate: dayjs(),
    dueDate: dayjs(),
    companyId: null,
    companyCode: '',
    companyName: '',
    companyContactId: null,
    shortName: '',
    incotermCity: '',
    companyWarning: '',
    currencyCode: null,
    exchangeRate: null,
    paymentMethodId: null,
    reference: '',
    projectId: null,
    costCenterCode: null,
    salesOrPurchasePersonId: null,
    billingAdressId: null,
    billingCountry: '',
    billingProvince: '',
    billingPostCode: '',
    billingCity: '',
    billingStreetAddress: '',
    shippingAdressId: null,
    shippingCountry: '',
    shippingProvince: '',
    shippingPostCode: '',
    shippingCity: '',
    shippingStreetAddress: '',
    deliveryMethodId: null,
    incotermCode: null,
    whsCode: selectedValues.whsCode,
    toWhsCode: null,
    priceListId: null,
    langCode: null,
    hunTaxNumber: null,
    euTaxNumber: null,
    foreignTaxNumber: null,
    sumInDocCur: null,
    netInDocCur: null,
    VATInDocCur: null,
    roundingInDocCur: null,
    sumInOwnCur: null,
    netInOwnCur: null,
    VATInOwnCur: null,
    roundingInOwnCur: null,
    note: '',
    internalNote: '',
    docLine: [],
  };

  const setQty = () => {
    if (!selectedValues?.productionItem) {
      return addNotification({
        content: 'Nincs rögzítve tétel!',
        type: NotificationType.WARNING,
      });
    }

    if (!selectedValues?.whsCode) {
      return addNotification({
        content: 'Nincs rögzítve fej szintű raktár!',
        type: NotificationType.WARNING,
      });
    }
    const emptyLineWhs = selectedValues.productionItem.find((itm) => !itm.whsCode);
    if (emptyLineWhs) {
      addNotification({
        content: 'Nincs rögzítve sor szintű raktár!',
        type: NotificationType.WARNING,
      });
    }

    const createDefaultBatchLine = (qty) => ({
      ...defaultBatchLine,
      id: uuidv4(),
      batchSn: selectedValues?.workingNumber ?? '',
      quantity: qty ?? '',
    });

    const remainingQty = subtract(
      Number(selectedValues.productionQuantity || 0),
      Number(selectedValues.completedQuantity || 0)
    );

    const newDocLines = [
      {
        ...defaultDocumentLine,
        id: selectedValues.id,
        itemCode: selectedValues.articleNumber,
        itemName: selectedValues.articleName,
        qty: remainingQty > 0 ? remainingQty : '',
        whsCode: selectedValues.whsCode,
        weight: selectedValues.weight || '',
        quantityUnitId: selectedValues.quantityUnit?.id,
        quantityUnitCode: selectedValues.quantityUnit.code,
        quantityUnitName: selectedValues.quantityUnit.name,
        baseId: selectedValues.id,
        baseLine: null,
        baseDocnum: selectedValues.workingNumber,
        baseDocType: null,
      },
      ...selectedValues.productionItem
        .filter((itm) => itm.secondaryProduct)
        .map((itm) => {
          return {
            ...defaultDocumentLine,
            id: itm.id,
            itemCode: itm.itemCode,
            itemName: itm.item?.itemName,
            qty: 0,
            whsCode: itm.whsCode,
            weight: itm.weight || '',
            quantityUnitId: itm.item?.quantityUnit?.id,
            quantityUnitCode: itm.item?.quantityUnit?.code,
            quantityUnitName: itm.item?.quantityUnit?.name,
            baseId: selectedValues.id,
            baseLine: itm.id,
            baseDocnum: selectedValues.workingNumber,
            baseDocType: null,
          };
        }),
    ].map((line) => {
      const item = autocompleteData?.items?.find((i) => i.itemCode === line.itemCode);
      const isBatchManaged = item?.invManagementMethod === InvManagementMethod.BATCH;

      if (!isBatchManaged) {
        return line;
      }

      const existingBatch =
        Array.isArray(line.docLineBatchSn) && line.docLineBatchSn.length ? line.docLineBatchSn[0] : null;
      const nextBatch = existingBatch
        ? [
            {
              ...existingBatch,
              batchSn: existingBatch.batchSn || (selectedValues?.workingNumber ?? ''),
              quantity: line.qty ?? '',
            },
          ]
        : [createDefaultBatchLine(line.qty)];

      return {
        ...line,
        docLineBatchSn: nextBatch,
      };
    });

    setDocLines(newDocLines);
  };

  useEffect(() => {
    if (Object.keys(selectedValues).length) {
      setQty();
    } else {
      setDocLines([]);
    }
  }, [selectedValues]);

  useEffect(() => {
    if (open) {
      productionService.getProductionById(cardData.productionid).then((data) => {
        setSelectedValues(data);
      });
    } else {
      setSelectedValues({});
    }
  }, [open]);

  const handleQuantityChange = (id, value) => {
    const parsed = parseFloat(value);
    setDocLines((prev) =>
      prev.map((line) => {
        if (line.id !== id) return line;
        if (Number.isNaN(parsed)) {
          return { ...line, qty: '' };
        }

        const item = autocompleteData?.items?.find((i) => i.itemCode === line.itemCode);
        const isBatchManaged = item?.invManagementMethod === InvManagementMethod.BATCH;

        const resolvedWorkingNumber = selectedValues?.workingNumber ?? '';

        const nextBatch = isBatchManaged
          ? Array.isArray(line.docLineBatchSn) && line.docLineBatchSn.length
            ? [
                {
                  ...line.docLineBatchSn[0],
                  batchSn: line.docLineBatchSn[0].batchSn || resolvedWorkingNumber,
                  quantity: parsed,
                },
              ]
            : [
                {
                  ...defaultBatchLine,
                  id: uuidv4(),
                  batchSn: resolvedWorkingNumber,
                  quantity: parsed,
                },
              ]
          : line.docLineBatchSn;

        const batchId = nextBatch?.[0]?.id ?? null;
        const nextBins =
          Array.isArray(line.docLineBin) && line.docLineBin.length
            ? line.docLineBin.map((bin) => {
                if (batchId && bin.snBinId !== batchId) return bin;
                return { ...bin, quantity: parsed };
              })
            : line.docLineBin;

        return {
          ...line,
          qty: parsed,
          docLineBatchSn: nextBatch,
          docLineBin: nextBins,
        };
      })
    );
  };

  const handleBatchEdit = (index) => {
    setBatchLine({ ...docLines[index], selectedLineIndex: index });
  };

  const isBatchManagedLine = (line) => {
    return (
      autocompleteData?.items?.find((item) => item.itemCode === line.itemCode)?.invManagementMethod ===
      InvManagementMethod.BATCH
    );
  };

  const handleBatchSave = (batchList) => {
    if (batchLine && typeof batchLine.selectedLineIndex === 'number') {
      setDocLines((prev) => {
        return prev.map((line, idx) => {
          if (idx === batchLine.selectedLineIndex) {
            const updatedBatch = batchList[0] ? { ...batchList[0], quantity: line.qty } : undefined;
            const nextBatch = updatedBatch ? [updatedBatch] : [];

            const whs = autocompleteData?.warehouse?.find((warehouse) => warehouse.code === line.whsCode);
            const isBinManaged = Boolean(whs?.binManaged);
            const isHeaderWhs = line.whsCode === selectedValues?.whsCode;
            const resolvedBin =
              isBinManaged && isHeaderWhs ? whs?.bin?.find((bin) => bin.binId === selectedValues?.binId) : null;

            const nextBins =
              isBinManaged && resolvedBin
                ? [
                    {
                      id: line.docLineBin?.[0]?.id ?? uuidv4(),
                      binCode: resolvedBin.binCode ?? '',
                      binId: resolvedBin.binId ?? null,
                      snBinId: updatedBatch?.id ?? null,
                      quantity: line.qty ?? '',
                    },
                  ]
                : line.docLineBin ?? [];

            return { ...line, docLineBatchSn: nextBatch, docLineBin: nextBins };
          }
          return line;
        });
      });
    }
    setBatchLine(null);
  };

  const handleLabelQuantityChange = (id, value) => {
    if (!/^\d*$/.test(value)) {
      return;
    }

    const nextValue = value === '' ? 1 : Math.max(1, Number.parseInt(value, 10));

    setDocLines((prev) => prev.map((line) => (line.id === id ? { ...line, labelQuantity: nextValue } : line)));
  };

  const handlePrintLabel = async (line) => {
    const batch = line.docLineBatchSn?.[0]?.batchSn?.trim() ?? '';

    if (!batch) {
      addNotification({
        content: 'Csak kitöltött sarzzsal lehet címkét nyomtatni!',
        type: NotificationType.WARNING,
      });
      return;
    }

    const labelQuantity = Number.parseInt(line.labelQuantity, 10);

    if (!Number.isInteger(labelQuantity) || labelQuantity < 1) {
      addNotification({
        content: 'A példányszám csak 1 vagy annál nagyobb egész szám lehet!',
        type: NotificationType.WARNING,
      });
      return;
    }

    const item = autocompleteData?.items?.find((itm) => itm.itemCode === line.itemCode);

    setPrintingLineId(line.id);

    try {
      const response = await itemService.createLabel({
        key: 3,
        itemCode: line.itemCode ?? '',
        itemName: line.itemName ?? '',
        quantity: 1,
        unitPrice: 0,
        qtyUnit: line.quantityUnitCode ?? item?.quantityUnit?.code ?? '',
        barCode: item?.barCode ?? '',
        batch,
        labelQuantity,
      });

      if (window.nativePrint) {
        printFromResponse(response, response.filename, labelQuantity);
        return;
      }

      downloadFile(response, response.filename);

      addNotification({
        content: 'Címke nyomtatás elindítva',
        type: NotificationType.SUCCESS,
      });
    } finally {
      setPrintingLineId(null);
    }
  };

  const handlePostDocument = async () => {
    if (isPosting) {
      return;
    }
    for (const line of docLines) {
      const item = autocompleteData?.items?.find((i) => i.itemCode === line.itemCode);
      const whs = autocompleteData?.warehouse?.find((warehouse) => warehouse.code === line.whsCode);
      const isBinManaged = Boolean(whs?.binManaged);
      const isHeaderWhs = line.whsCode === selectedValues?.whsCode;
      const resolvedBin =
        isBinManaged && isHeaderWhs ? whs?.bin?.find((bin) => bin.binId === selectedValues?.binId) : null;
      if (item && item.invManagementMethod === InvManagementMethod.BATCH) {
        const batchQty = line.docLineBatchSn && line.docLineBatchSn[0] ? Number(line.docLineBatchSn[0].quantity) : 0;
        if (Number(line.qty) !== batchQty) {
          addNotification({
            content: `A(z) ${line.itemCode} cikkszámú tételhez megadott sarzs mennyisége (${batchQty}) nem egyezik a lejelentett mennyiséggel (${line.qty})!`,
            type: NotificationType.ERROR,
          });
          return;
        }

        if (isBinManaged && isHeaderWhs && !resolvedBin?.binId) {
          addNotification({
            content: `A(z) ${line.itemCode} cikkszámú tételhez rakhely megadása kötelező!`,
            type: NotificationType.ERROR,
          });
          return;
        }
      }

      if (isBinManaged && isHeaderWhs && !resolvedBin?.binId) {
        addNotification({
          content: `A(z) ${line.itemCode} cikkszámú tételhez rakhely megadása kötelező!`,
          type: NotificationType.ERROR,
        });
        return;
      }
    }

    const document = {
      ...cleanValues(defaultValues),
      docLine: docLines
        .filter((l) => l.qty > 0)
        .map((line) => {
          const item = autocompleteData?.items?.find((i) => i.itemCode === line.itemCode);
          const isBatchManaged = item?.invManagementMethod === InvManagementMethod.BATCH;
          const whs = autocompleteData?.warehouse?.find((warehouse) => warehouse.code === line.whsCode);
          const isBinManaged = Boolean(whs?.binManaged);
          const isHeaderWhs = line.whsCode === selectedValues?.whsCode;
          const resolvedBin =
            isBinManaged && isHeaderWhs ? whs?.bin?.find((bin) => bin.binId === selectedValues?.binId) : null;
          const batchId = line.docLineBatchSn?.[0]?.id ?? null;

          const docLineBin =
            isBinManaged && isHeaderWhs && resolvedBin
              ? [
                  {
                    id: line.docLineBin?.[0]?.id ?? uuidv4(),
                    binCode: resolvedBin.binCode ?? '',
                    binId: resolvedBin.binId ?? null,
                    snBinId: isBatchManaged ? batchId : null,
                    quantity: line.qty ?? '',
                  },
                ]
              : [];

          return {
            ...cleanValues(line),
            docLineBatchSn: line.docLineBatchSn.map((batch) => ({
              ...cleanValues(batch),
            })),
            docLineBin: docLineBin.map((bin) => ({
              ...cleanValues(bin),
            })),
          };
        }),
    };

    setIsPosting(true);

    try {
      const data = await productionService.createProductionReceipt(document, selectedValues.id);

      onClose();
      setReportData(defReportData);
      setSelectedValues(data);

      const reportValues = {
        quantity: docLines.reduce((acc, line) => acc + Number(line.qty || 0), 0),
        scrapQuantity: reportData.scrapQuantity,
        scrapReasonId: reportData.scrapReasonId,
        description: reportData.description,
        doseNumber: '',
      };

      // ide kell még a megállítás/lezárás logika
      if (mode === 'interrupt') {
        handleInterruptOperation(null, reportValues);
      } else {
        handleFinishOperation(reportValues);
      }
    } catch (_e) {
      addNotification({
        content: 'A bevételezés könyvelése nem sikerült.',
        type: NotificationType.ERROR,
      });
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <DialogUI
      open={open}
      onClose={onClose}
      headerContent={'Bevételezés'}
      maxWidth="2xl"
      fullWidth
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            mx: 0.5,
          },
        },
        '& .MuiDialogContent-root': {
          pl: 0.5,
          pr: 1.5,
        },
      }}
    >
      {isMobile ? (
        <ProductionReceiptMobileCards
          docLines={docLines}
          selectedValues={selectedValues}
          cardData={cardData}
          reportData={reportData}
          setReportData={setReportData}
          autoCompleteOptions={autoCompleteOptions}
          printingLineId={printingLineId}
          handleQuantityChange={handleQuantityChange}
          handleBatchEdit={handleBatchEdit}
          handleLabelQuantityChange={handleLabelQuantityChange}
          handlePrintLabel={handlePrintLabel}
          isBatchManagedLine={isBatchManagedLine}
        />
      ) : (
        <Table style={{ marginTop: 16 }}>
          <TableHead sx={{ background: ui.tableHeader }}>
            <TableRow>
              <TableCell>Cikkszám</TableCell>
              <TableCell>Megnevezés</TableCell>
              <TableCell>Gyártandó / Eddig készletre vett mennyiség</TableCell>
              <TableCell>Mennyiség</TableCell>
              <TableCell>Me.</TableCell>
              <TableCell>Selejt mennyiség</TableCell>
              <TableCell>Selejt ok</TableCell>
              <TableCell>Megjegyzés</TableCell>
              <TableCell>Sarzs</TableCell>
              <TableCell>Példányszám</TableCell>
              <TableCell>Címkenyomtatás</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {docLines.length > 1 ? (
              <>
                <TableRow key={docLines[0].id}>
                  <TableCell>{docLines[0].itemCode}</TableCell>
                  <TableCell>{docLines[0].itemName}</TableCell>
                  <TableCell>{`${selectedValues.productionQuantity} ${cardData.articleQtyCode} / ${selectedValues.completedQuantity} ${cardData.articleQtyCode}`}</TableCell>
                  <TableCell style={{ maxWidth: 120 }}>
                    <InputUI
                      type="number"
                      value={docLines[0].qty}
                      onChange={(e) => handleQuantityChange(docLines[0].id, e.target.value)}
                      inputProps={{ min: 0 }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{docLines[0].quantityUnitCode}</TableCell>
                  <TableCell style={{ maxWidth: 120 }}>
                    <InputUI
                      type="number"
                      value={reportData.scrapQuantity}
                      onChange={(e) =>
                        setReportData((prev) => ({
                          ...prev,
                          scrapQuantity: e.target.value,
                        }))
                      }
                      inputProps={{ min: 0 }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell style={{ maxWidth: 150 }}>
                    <AutoCompleteSelectUI
                      onChange={(_e, newVal, reason) => {
                        setReportData((prev) => ({
                          ...prev,
                          scrapReasonId: reason === 'clear' ? null : newVal?.value,
                        }));
                      }}
                      selectedValue={reportData.scrapReasonId}
                      variant="outlined"
                      size="small"
                      dataset={autoCompleteOptions.scrap}
                    />
                  </TableCell>
                  <TableCell style={{ maxWidth: 200 }}>
                    <InputUI
                      value={reportData.description}
                      onChange={(e) =>
                        setReportData((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      size="small"
                      multiline
                      rows={2}
                    />
                  </TableCell>
                  <TableCell>
                    {isBatchManagedLine(docLines[0]) && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <IconButton size="small" onClick={() => handleBatchEdit(0)}>
                          <EditIcon />
                        </IconButton>
                        <Typography variant="body2">{docLines[0].docLineBatchSn?.[0]?.batchSn ?? ''}</Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell style={{ maxWidth: 120 }} sx={labelPrintCellSx}>
                    {isBatchManagedLine(docLines[0]) && (
                      <InputUI
                        type="number"
                        value={docLines[0].labelQuantity}
                        onChange={(e) => handleLabelQuantityChange(docLines[0].id, e.target.value)}
                        inputProps={{ min: 1, step: 1 }}
                        size="small"
                        sx={labelQuantityInputSx}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={labelPrintCellSx}>
                    {isBatchManagedLine(docLines[0]) && (
                      <ButtonUI
                        text={printingLineId === docLines[0].id ? 'Nyomtatás...' : 'Nyomtatás'}
                        className="bg-success"
                        onClick={() => handlePrintLabel(docLines[0])}
                        disabled={
                          !docLines[0].docLineBatchSn?.[0]?.batchSn?.trim() || printingLineId === docLines[0].id
                        }
                      />
                    )}
                  </TableCell>
                </TableRow>
                <TableRow key="selejt-row">
                  <TableCell colSpan={10} style={{ fontWeight: 'bold', textAlign: 'left' }}>
                    Selejt
                  </TableCell>
                </TableRow>
                {docLines.slice(1).map((itm, idx) => (
                  <TableRow key={itm.id}>
                    <TableCell>{itm.itemCode}</TableCell>
                    <TableCell>{itm.itemName}</TableCell>
                    <TableCell></TableCell>
                    <TableCell style={{ maxWidth: 120 }}>
                      <InputUI
                        type="number"
                        value={itm.qty}
                        onChange={(e) => handleQuantityChange(itm.id, e.target.value)}
                        inputProps={{ min: 0 }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{itm.quantityUnitCode}</TableCell>
                    <TableCell style={{ maxWidth: 120 }}>
                      <InputUI
                        type="number"
                        value={reportData.scrapQuantity}
                        onChange={(e) =>
                          setReportData((prev) => ({
                            ...prev,
                            scrapQuantity: e.target.value,
                          }))
                        }
                        inputProps={{ min: 0 }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell style={{ maxWidth: 150 }}>
                      <AutoCompleteSelectUI
                        onChange={(_e, newVal, reason) => {
                          setReportData((prev) => ({
                            ...prev,
                            scrapReasonId: reason === 'clear' ? null : newVal?.value,
                          }));
                        }}
                        selectedValue={reportData.scrapReasonId}
                        variant="outlined"
                        size="small"
                        dataset={autoCompleteOptions.scrap}
                      />
                    </TableCell>
                    <TableCell style={{ maxWidth: 200 }}>
                      <InputUI
                        value={reportData.description}
                        onChange={(e) =>
                          setReportData((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                        size="small"
                        multiline
                        rows={2}
                      />
                    </TableCell>
                    <TableCell>
                      {isBatchManagedLine(itm) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <IconButton size="small" onClick={() => handleBatchEdit(idx + 1)}>
                            <EditIcon />
                          </IconButton>
                          <Typography variant="body2">{itm.docLineBatchSn?.[0]?.batchSn ?? ''}</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell style={{ maxWidth: 120 }} sx={labelPrintCellSx}>
                      {isBatchManagedLine(itm) && (
                        <InputUI
                          type="number"
                          value={itm.labelQuantity}
                          onChange={(e) => handleLabelQuantityChange(itm.id, e.target.value)}
                          inputProps={{ min: 1, step: 1 }}
                          size="small"
                          sx={labelQuantityInputSx}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={labelPrintCellSx}>
                      {isBatchManagedLine(itm) && (
                        <ButtonUI
                          text={printingLineId === itm.id ? 'Nyomtatás...' : 'Nyomtatás'}
                          className="bg-success"
                          onClick={() => handlePrintLabel(itm)}
                          disabled={!itm.docLineBatchSn?.[0]?.batchSn?.trim() || printingLineId === itm.id}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : (
              docLines.map((itm, idx) => (
                <TableRow key={itm.id}>
                  <TableCell>{itm.itemCode}</TableCell>
                  <TableCell>{itm.itemName}</TableCell>
                  <TableCell>{`${selectedValues.productionQuantity} ${cardData.articleQtyCode} / ${selectedValues.completedQuantity} ${cardData.articleQtyCode}`}</TableCell>
                  <TableCell style={{ maxWidth: 120 }}>
                    <InputUI
                      type="number"
                      value={itm.qty}
                      onChange={(e) => handleQuantityChange(itm.id, e.target.value)}
                      inputProps={{ min: 0 }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{itm.quantityUnitCode}</TableCell>
                  <TableCell style={{ maxWidth: 120 }}>
                    <InputUI
                      type="number"
                      value={reportData.scrapQuantity}
                      onChange={(e) =>
                        setReportData((prev) => ({
                          ...prev,
                          scrapQuantity: e.target.value,
                        }))
                      }
                      inputProps={{ min: 0 }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell style={{ maxWidth: 150 }}>
                    <AutoCompleteSelectUI
                      onChange={(_e, newVal, reason) => {
                        setReportData((prev) => ({
                          ...prev,
                          scrapReasonId: reason === 'clear' ? null : newVal?.value,
                        }));
                      }}
                      selectedValue={reportData.scrapReasonId}
                      variant="outlined"
                      size="small"
                      dataset={autoCompleteOptions.scrap}
                    />
                  </TableCell>
                  <TableCell style={{ maxWidth: 200 }}>
                    <InputUI
                      value={reportData.description}
                      onChange={(e) =>
                        setReportData((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      size="small"
                      multiline
                      rows={2}
                    />
                  </TableCell>
                  <TableCell>
                    {isBatchManagedLine(itm) && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <IconButton size="small" onClick={() => handleBatchEdit(idx)}>
                          <EditIcon />
                        </IconButton>
                        <Typography variant="body2">{itm.docLineBatchSn?.[0]?.batchSn ?? ''}</Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell style={{ maxWidth: 120 }} sx={labelPrintCellSx}>
                    {isBatchManagedLine(itm) && (
                      <InputUI
                        type="number"
                        value={itm.labelQuantity}
                        onChange={(e) => handleLabelQuantityChange(itm.id, e.target.value)}
                        inputProps={{ min: 1, step: 1 }}
                        size="small"
                        sx={labelQuantityInputSx}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={labelPrintCellSx}>
                    {isBatchManagedLine(itm) && (
                      <ButtonUI
                        text={printingLineId === itm.id ? 'Nyomtatás...' : 'Nyomtatás'}
                        className="bg-success"
                        onClick={() => handlePrintLabel(itm)}
                        disabled={!itm.docLineBatchSn?.[0]?.batchSn?.trim() || printingLineId === itm.id}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
      {batchLine && (
        <ProductionBatchDialog
          open={!!batchLine}
          onClose={() => setBatchLine(null)}
          batchList={docLines[batchLine.selectedLineIndex]?.docLineBatchSn || []}
          defaultBatchSn={selectedValues?.workingNumber ?? ''}
          onSave={(batchList) => handleBatchSave(batchList)}
        />
      )}

      <Box sx={{ px: 4, mt: 8, display: 'flex', justifyContent: 'center', gap: 3 }}>
        <ButtonUI
          text={isPosting ? 'Könyvelés...' : 'Könyvelés'}
          className="bg-success"
          type="submit"
          onClick={() => handlePostDocument()}
          disabled={isPosting}
        ></ButtonUI>
        <ButtonUI
          text="Mégse"
          className="bg-"
          color="blue-gray"
          type="button"
          variant="gradient"
          onClick={onClose}
          disabled={isPosting}
        ></ButtonUI>
      </Box>
    </DialogUI>
  );
};

export default ProductionReceiptDialog;
